/**
 * Hoppie ACARS telexes. The Rust desk owns the logon and the socket.
 * This module is the text: templates, the downlink parser, and the trip match.
 *
 * Hoppie's network is public. Templates carry no prices, names, or mail.
 * Fuel figures in a telex are whole pounds.
 *
 * Protocol notes used here:
 * https://www.hoppie.nl/acars/system/tech.html
 * Poll body shape also checked against https://docs.magknight.org/hoppie/
 */
import { displayFlightNumber } from "./flightNumber";
import { confirmedDelays, confirmedUplifts, delayTelexFields, fileDelay, fileFuelUplift, hhmmZ } from "./tripDelayFuel";
import { normalizeReportedDelayCode } from "./delayCodes";
import type { AcarsChannel, AcarsMsg, Mission, UplinkKind } from "./types";

export const TELEX_MAX = 220;
export const ACARS_MATCH_WINDOW_MS = 12 * 60 * 60 * 1000;
export const ACARS_REPLAN_GAP_MS = 60_000;
export const ACARS_SEND_GAP_MS = 3_000;
export const ACARS_SENDS_PER_HOUR = 6;
export const HOPPIE_REGISTER_URL = "https://www.hoppie.nl/acars/system/register.html";
export const HOPPIE_CONNECT_URL = "https://www.hoppie.nl/acars/system/connect.html";

export const UPLINK_KINDS = [
  "delay",
  "etd_change",
  "slot",
  "pax_change",
  "wx_advisory",
  "fuel_advisory",
  "mx_call",
  "free_text",
] as const satisfies readonly UplinkKind[];

export type HoppieNetwork = "vatsim" | "ivao" | "none";

export type HoppieDeskSettings = {
  enabled: boolean;
  network: HoppieNetwork;
  station: string;
  autoSend: boolean;
  configured: boolean;
  logonLast4: string;
  promptSeen: boolean;
};

export const DEFAULT_HOPPIE_DESK: HoppieDeskSettings = {
  enabled: false,
  network: "vatsim",
  station: "",
  autoSend: false,
  configured: false,
  logonLast4: "",
  promptSeen: false,
};

const KNOWN_AIRCRAFT = [
  "FlyByWire A32NX (TELEX and CPDLC)",
  "Fenix A320 (CPDLC and PDC)",
  "iniBuilds A350 (Hoppie ACARS and D-ATIS)",
  "PMDG 737 / 777",
  "Horizon 787",
  "FlightFX Hawker 800XP with the community FFX Hawker 800XP ACARS/CPDLC add-on",
  "Garmin G3000/G5000 CPDLC add-on on supported types",
];

export function acarsCapableHint(): string {
  return `Known Hoppie aircraft: ${KNOWN_AIRCRAFT.join("; ")}. A generic ACARS page does not mean company telex works. Leave this off until the aircraft manual says it does.`;
}

export function validStation(raw: unknown): string | null {
  const station = String(raw ?? "").trim().toUpperCase();
  if (!/^[A-Z0-9]{3,8}$/.test(station)) return null;
  return station;
}

export function defaultStation(flightPrefix: unknown): string {
  const prefix = String(flightPrefix ?? "").trim().toUpperCase().replace(/[^A-Z0-9]/g, "");
  return validStation(`${prefix}OPS`) ?? "";
}

export function hydrateHoppieDesk(raw: unknown): HoppieDeskSettings {
  const row = raw && typeof raw === "object" ? (raw as Partial<HoppieDeskSettings>) : {};
  const network: HoppieNetwork = row.network === "ivao" || row.network === "none" ? row.network : "vatsim";
  const last4 = String(row.logonLast4 ?? "").replace(/[^A-Za-z0-9]/g, "").slice(-4);
  return {
    enabled: row.enabled === true,
    network,
    station: validStation(row.station) ?? "",
    autoSend: row.autoSend === true,
    configured: row.configured === true && last4.length === 4,
    logonLast4: last4.length === 4 ? last4 : "",
    promptSeen: row.promptSeen === true,
  };
}

export function logonLast4(code: string): string {
  const digits = code.replace(/\s+/g, "");
  return digits.slice(-4);
}

/** Drop logon values, bearer tokens, and long tokens before any log line. */
export function redactHoppieText(input: string, code = ""): string {
  let out = input.replace(/logon=[^&\s]+/gi, "logon=[redacted]");
  out = out.replace(/bearer\s+\S+/gi, "bearer [redacted]");
  out = out.replace(/xai-[A-Za-z0-9_-]+/gi, "[redacted]");
  const secret = code.trim();
  if (secret.length >= 8) out = out.split(secret).join("[redacted]");
  return out;
}

function asciiTelex(raw: string): string {
  return raw
    .toUpperCase()
    .replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+/g, " ")
    .replace(/[$€£]\s?\d[\d,]*(?:\.\d+)?/g, " ")
    .replace(/[^A-Z0-9 +\-./]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function isUplinkKind(raw: unknown): raw is UplinkKind {
  return typeof raw === "string" && (UPLINK_KINDS as readonly string[]).includes(raw);
}

const REQUIRED: Record<UplinkKind, readonly string[]> = {
  delay: ["code", "minutes", "etd", "origin", "dest", "short"],
  etd_change: ["old", "new", "origin", "dest"],
  slot: ["ctot", "origin"],
  pax_change: ["old", "new"],
  wx_advisory: ["icao", "cue"],
  fuel_advisory: ["lb", "icao"],
  mx_call: ["reg", "icao", "squawk"],
  free_text: ["text"],
};

export function notifyParamsOk(template: UplinkKind, params: Record<string, string> | undefined): boolean {
  const bag = params ?? {};
  return REQUIRED[template].every((key) => String(bag[key] ?? "").trim().length > 0);
}

function fill(template: string, params: Record<string, string>): string {
  return template.replace(/\{([A-Z0-9]+)\}/g, (_, key: string) => asciiTelex(params[key.toLowerCase()] ?? params[key] ?? ""));
}

export function renderTelexBody(kind: UplinkKind, params: Record<string, string>): { ok: true; body: string } | { ok: false; error: string } {
  if (!isUplinkKind(kind)) return { ok: false, error: "Unknown telex template." };
  if (!notifyParamsOk(kind, params)) return { ok: false, error: "That telex is missing a figure from the trip." };
  let body = "";
  switch (kind) {
    case "delay":
      body = fill("DLY {CODE} {MINUTES}MIN NEW ETD {ETD}Z {ORIGIN}-{DEST} RSN {SHORT}", params);
      break;
    case "etd_change":
      body = fill("ETD REV {OLD}Z TO {NEW}Z {ORIGIN}-{DEST}", params);
      break;
    case "slot":
      body = fill("SLOT CTOT {CTOT}Z {ORIGIN}", params);
      break;
    case "pax_change":
      body = fill("PAX REV {OLD} TO {NEW} PLS CHK FUEL/WB", params);
      break;
    case "wx_advisory":
      body = fill("WX ADVSY {ICAO} {CUE}", params);
      break;
    case "fuel_advisory":
      body = fill("FUEL ADVSY MIN BLOCK {LB}LB AT {ICAO}", params);
      break;
    case "mx_call":
      body = fill("MX CALL TAIL {REG} RTB {ICAO} AFTER THIS LEG REF {SQUAWK}", params);
      break;
    case "free_text":
      body = asciiTelex(params.text ?? "");
      break;
    default:
      return { ok: false, error: "Unknown telex template." };
  }
  body = asciiTelex(body);
  if (!body) return { ok: false, error: "Telex was empty after it was cleaned." };
  return { ok: true, body };
}

export function renderUplink(kind: UplinkKind, params: Record<string, string>, station: string): { ok: true; parts: string[] } | { ok: false; error: string; companionOnly?: boolean } {
  const body = renderTelexBody(kind, params);
  if (!body.ok) return body;
  const st = validStation(station);
  if (!st) return { ok: false, error: "Station callsign must be 3 to 8 letters or digits.", companionOnly: true };
  if (body.body.length <= TELEX_MAX) return { ok: true, parts: [body.body] };
  const budget = TELEX_MAX - 4;
  const first = body.body.slice(0, budget).trim();
  const second = body.body.slice(budget).trim();
  if (!second) return { ok: true, parts: [first] };
  const part1 = `1/2 ${first}`.slice(0, TELEX_MAX);
  const part2 = `2/2 ${second}`.slice(0, TELEX_MAX);
  if (second.length + 4 > TELEX_MAX) return { ok: false, error: "Telex is longer than two parts. It stays on the companion only.", companionOnly: true };
  return { ok: true, parts: [part1, part2] };
}

export type Downlink =
  | { kind: "delay"; code: string; minutes: string; etd: string; remarks: string; rawCode: string }
  | { kind: "fuel"; amount: string; unit: string; airport: string }
  | { kind: "mx"; ata: string; text: string }
  | { kind: "progress"; summary: string }
  | { kind: "cpdlc"; text: string }
  | { kind: "text"; text: string };

export function parseDownlink(packet: string, hoppieType = "telex"): Downlink {
  const type = hoppieType.trim().toLowerCase();
  const text = packet.replace(/\s+/g, " ").trim();
  if (type === "cpdlc") return { kind: "cpdlc", text: text.slice(0, 220) };
  if (type === "progress") {
    const summary = text.replace(/[^A-Za-z0-9: ]/g, " ").replace(/\s+/g, " ").trim().slice(0, 80);
    return { kind: "progress", summary: summary || "PROGRESS" };
  }
  const delay = text.match(/^DLY\s+([A-Z0-9]{1,4})\s+(\d{1,4})(?:\s+ETD\s*(\d{4}))?(?:\s+RMK\s+(.+))?$/i);
  if (delay) {
    const reported = normalizeReportedDelayCode(delay[1]);
    const minutes = String(Math.min(24 * 60, Math.max(0, Math.round(Number(delay[2])))));
    return {
      kind: "delay",
      code: reported.code,
      minutes,
      etd: delay[3] ?? "",
      remarks: (delay[4] ?? "").trim().slice(0, 160),
      rawCode: reported.rawCode ?? "",
    };
  }
  const fuel = text.match(/^FUEL\s+(\d+(?:\.\d+)?)\s*(KG|LB|L|USG)(?:\s+AT\s+([A-Z0-9]{4}))?$/i);
  if (fuel) {
    return { kind: "fuel", amount: fuel[1] ?? "", unit: (fuel[2] ?? "LB").toUpperCase(), airport: (fuel[3] ?? "").toUpperCase() };
  }
  const mx = text.match(/^MX(?:\s+ATA\s+(\d{2}))?\s+(.+)$/i);
  if (mx) return { kind: "mx", ata: mx[1] ?? "", text: (mx[2] ?? "").trim().slice(0, 160) };
  return { kind: "text", text: text.slice(0, 220) };
}

export function downlinkToParsed(row: Downlink): Record<string, string> {
  const out: Record<string, string> = { kind: row.kind };
  if (row.kind === "delay") {
    out.code = row.code;
    out.minutes = row.minutes;
    if (row.etd) out.etd = row.etd;
    if (row.remarks) out.remarks = row.remarks;
    if (row.rawCode) out.rawCode = row.rawCode;
  } else if (row.kind === "fuel") {
    out.amount = row.amount;
    out.unit = row.unit;
    if (row.airport) out.airport = row.airport;
  } else if (row.kind === "mx") {
    if (row.ata) out.ata = row.ata;
    out.text = row.text;
  } else if (row.kind === "progress") {
    out.summary = row.summary;
  } else if (row.kind === "cpdlc") {
    out.text = row.text;
  } else {
    out.text = row.text;
  }
  return out;
}

export type HoppieInbound = { from: string; type: string; packet: string };

export type HoppieBody =
  | { ok: true; messages: HoppieInbound[] }
  | { ok: false; error: string; callsignInUse?: boolean; illegalLogon?: boolean };

function readBrace(input: string, start: number): { inner: string; end: number } | null {
  if (input[start] !== "{") return null;
  let depth = 0;
  for (let i = start; i < input.length; i += 1) {
    if (input[i] === "{") depth += 1;
    else if (input[i] === "}") {
      depth -= 1;
      if (depth === 0) return { inner: input.slice(start + 1, i), end: i + 1 };
    }
  }
  return null;
}

function stripNumericPrefix(inner: string): string {
  return inner.replace(/^\s*\d+\s+/, "").trim();
}

export function parseHoppieResponse(body: string): HoppieBody {
  const text = body.replace(/^\uFEFF/, "").trim();
  if (!text) return { ok: false, error: "Empty Hoppie response." };
  const head = text.slice(0, 5).toLowerCase();
  if (head.startsWith("error")) {
    const brace = text.indexOf("{");
    const block = brace >= 0 ? readBrace(text, brace) : null;
    const reason = stripNumericPrefix(block?.inner ?? text.slice(5)).replace(/[{}]/g, " ").replace(/\s+/g, " ").trim();
    const lower = reason.toLowerCase();
    return {
      ok: false,
      error: reason || "Hoppie error.",
      callsignInUse: lower.includes("callsign already in use"),
      illegalLogon: lower.includes("illegal logon") || lower.includes("invalid logon"),
    };
  }
  if (!head.startsWith("ok")) return { ok: false, error: "Hoppie response was not ok or error." };
  const rest = text.slice(2).trim();
  if (!rest) return { ok: true, messages: [] };
  const messages: HoppieInbound[] = [];
  let i = 0;
  while (i < rest.length) {
    if (rest[i] !== "{") {
      i += 1;
      continue;
    }
    const block = readBrace(rest, i);
    if (!block) break;
    i = block.end;
    const inner = stripNumericPrefix(block.inner);
    const nestedAt = inner.indexOf("{");
    if (nestedAt < 0) continue;
    const headPart = inner.slice(0, nestedAt).trim();
    const packetBlock = readBrace(inner, nestedAt);
    if (!packetBlock) continue;
    const bits = headPart.split(/\s+/);
    const from = (bits[0] ?? "").toUpperCase();
    const type = (bits[1] ?? "telex").toLowerCase();
    if (!from) continue;
    messages.push({ from, type, packet: packetBlock.inner.trim() });
  }
  return { ok: true, messages };
}

export function normCallsign(raw: unknown): string {
  return String(raw ?? "").toUpperCase().replace(/[^A-Z0-9]/g, "");
}

export type AcarsTripMatch = {
  id: string;
  flightNumber?: string;
  callsign: string;
  callsignIcao?: string;
  departureIso: string;
  status?: string;
};

export function tripCallsigns(trip: AcarsTripMatch): string[] {
  return [displayFlightNumber(trip), trip.callsign, trip.callsignIcao ?? ""]
    .map(normCallsign)
    .filter((row) => row.length >= 3);
}

export function matchAcarsCallsign(from: string, trips: readonly AcarsTripMatch[], now: Date): { missionId: string } | { unmatched: true } {
  const call = normCallsign(from);
  if (!call) return { unmatched: true };
  const nowMs = now.getTime();
  const hits = trips.filter((trip) => {
    if (!tripCallsigns(trip).includes(call)) return false;
    const std = Date.parse(trip.departureIso);
    if (!Number.isFinite(std)) return false;
    return Math.abs(std - nowMs) <= ACARS_MATCH_WINDOW_MS;
  });
  if (hits.length === 0) return { unmatched: true };
  if (hits.length === 1) return { missionId: hits[0]!.id };
  const ranked = [...hits].sort((a, b) => {
    const da = Math.abs(Date.parse(a.departureIso) - nowMs);
    const db = Math.abs(Date.parse(b.departureIso) - nowMs);
    if (da !== db) return da - db;
    const rank = (status?: string) => (status === "in-progress" ? 0 : status === "pending" ? 1 : 2);
    return rank(a.status) - rank(b.status);
  });
  const top = ranked[0]!;
  const next = ranked[1]!;
  if (Math.abs(Date.parse(top.departureIso) - nowMs) === Math.abs(Date.parse(next.departureIso) - nowMs) && top.status === next.status) {
    return { unmatched: true };
  }
  return { missionId: top.id };
}

export function acarsReplanAllowed(lastAt: string | null | undefined, now: Date): { run: boolean; nextAt: string } {
  const last = lastAt ? Date.parse(lastAt) : NaN;
  if (Number.isFinite(last) && now.getTime() - last < ACARS_REPLAN_GAP_MS) {
    return { run: false, nextAt: new Date(last).toISOString() };
  }
  return { run: true, nextAt: now.toISOString() };
}

export function sendSlotOpen(input: { lastSentAtMs: number | null; nowMs: number; recentSentAtMs: number[] }): { ok: true } | { ok: false; waitMs: number; reason: string } {
  if (input.lastSentAtMs != null && input.nowMs - input.lastSentAtMs < ACARS_SEND_GAP_MS) {
    return { ok: false, waitMs: ACARS_SEND_GAP_MS - (input.nowMs - input.lastSentAtMs), reason: "Hoppie sends stay 3 seconds apart." };
  }
  const hour = input.recentSentAtMs.filter((at) => input.nowMs - at < 3_600_000);
  if (hour.length >= ACARS_SENDS_PER_HOUR) {
    const oldest = Math.min(...hour);
    return { ok: false, waitMs: Math.max(0, 3_600_000 - (input.nowMs - oldest)), reason: "Six telexes an hour is the cap for this aircraft." };
  }
  return { ok: true };
}

export function dropStaleUnmatched<T extends { atUtc: string }>(rows: readonly T[], now: Date): T[] {
  const cutoff = now.getTime() - 24 * 60 * 60 * 1000;
  return rows.filter((row) => {
    const at = Date.parse(row.atUtc);
    return Number.isFinite(at) && at >= cutoff;
  });
}

export type NotifyDraft = {
  id: string;
  missionId: string;
  template: UplinkKind;
  params: Record<string, string>;
};

type NotifyMission = Pick<
  Mission,
  | "id"
  | "callsign"
  | "flightNumber"
  | "originIcao"
  | "destIcao"
  | "departureIso"
  | "registration"
  | "status"
  | "paxCount"
  | "fuelLbs"
  | "releasePack"
  | "delays"
  | "fuelUplifts"
  | "slotCtot"
  | "releasedDepartureIso"
  | "releasedPaxCount"
  | "releasedFuelLbs"
  | "wxAdvisory"
  | "acarsLog"
  | "aircraftId"
>;

function alreadyQueued(log: AcarsMsg[] | undefined, template: UplinkKind, key: string): boolean {
  return (log ?? []).some((row) => row.dir === "up" && row.kind === template && row.packet.includes(key) && row.status !== "failed");
}

function minutesBetween(a: string, b: string): number | null {
  const ta = Date.parse(a);
  const tb = Date.parse(b);
  if (!Number.isFinite(ta) || !Number.isFinite(tb)) return null;
  return Math.round((tb - ta) / 60_000);
}

export function dispatchNotifyDrafts(input: { missions: readonly NotifyMission[]; groundedAircraftIds?: readonly string[] }): NotifyDraft[] {
  const grounded = new Set(input.groundedAircraftIds ?? []);
  const out: NotifyDraft[] = [];
  for (const mission of input.missions) {
    if (mission.status === "cancelled" || mission.status === "completed") continue;
    const released = mission.releasePack?.status === "released" || mission.status === "in-progress";
    const delay = confirmedDelays(mission)[0];
    if (delay && !alreadyQueued(mission.acarsLog, "delay", `DLY ${delay.code}`)) {
      out.push({
        id: `notify:${mission.id}:delay`,
        missionId: mission.id,
        template: "delay",
        params: delayTelexFields(delay, mission),
      });
    } else if (released && mission.releasedDepartureIso) {
      const slip = minutesBetween(mission.releasedDepartureIso, mission.departureIso);
      if (slip != null && Math.abs(slip) >= 15 && !alreadyQueued(mission.acarsLog, "etd_change", "ETD REV")) {
        out.push({
          id: `notify:${mission.id}:etd`,
          missionId: mission.id,
          template: "etd_change",
          params: {
            old: hhmmZ(mission.releasedDepartureIso).replace(/Z$/, ""),
            new: hhmmZ(mission.departureIso).replace(/Z$/, ""),
            origin: mission.originIcao,
            dest: mission.destIcao,
          },
        });
      }
    }
    if (mission.slotCtot && /^\d{4}$/.test(mission.slotCtot) && !alreadyQueued(mission.acarsLog, "slot", "SLOT CTOT")) {
      out.push({
        id: `notify:${mission.id}:slot`,
        missionId: mission.id,
        template: "slot",
        params: { ctot: mission.slotCtot, origin: mission.originIcao },
      });
    }
    if (released && mission.releasedPaxCount != null && mission.releasedPaxCount !== mission.paxCount && !alreadyQueued(mission.acarsLog, "pax_change", "PAX REV")) {
      out.push({
        id: `notify:${mission.id}:pax`,
        missionId: mission.id,
        template: "pax_change",
        params: { old: String(mission.releasedPaxCount), new: String(mission.paxCount) },
      });
    }
    if (mission.wxAdvisory?.cue && mission.wxAdvisory.icao && !alreadyQueued(mission.acarsLog, "wx_advisory", "WX ADVSY")) {
      out.push({
        id: `notify:${mission.id}:wx`,
        missionId: mission.id,
        template: "wx_advisory",
        params: { icao: mission.wxAdvisory.icao, cue: mission.wxAdvisory.cue },
      });
    }
    const uplift = confirmedUplifts(mission)[0];
    const fuelChanged = released && mission.releasedFuelLbs != null && Math.round(mission.releasedFuelLbs) !== Math.round(mission.fuelLbs);
    if ((uplift || fuelChanged) && !alreadyQueued(mission.acarsLog, "fuel_advisory", "FUEL ADVSY")) {
      const lb = uplift ? uplift.pounds : Math.round(mission.fuelLbs);
      const icao = uplift?.airportIcao || mission.originIcao;
      if (lb > 0 && icao) {
        out.push({
          id: `notify:${mission.id}:fuel`,
          missionId: mission.id,
          template: "fuel_advisory",
          params: { lb: String(lb), icao },
        });
      }
    }
    if (grounded.has(mission.aircraftId) && mission.registration && !alreadyQueued(mission.acarsLog, "mx_call", "MX CALL")) {
      out.push({
        id: `notify:${mission.id}:mx`,
        missionId: mission.id,
        template: "mx_call",
        params: { reg: mission.registration, icao: mission.destIcao || mission.originIcao, squawk: "MX" },
      });
    }
  }
  return out;
}

export function companionDownlink(input: {
  kind: "delay" | "mx" | "fuel" | "free_text";
  code?: string;
  minutes?: number;
  etd?: string;
  remarks?: string;
  ata?: string;
  text?: string;
  amount?: number;
  unit?: "lb" | "kg" | "L" | "USG";
  airport?: string;
}): { ok: true; packet: string; parsed: Downlink } | { ok: false; error: string } {
  if (input.kind === "delay") {
    const code = normalizeReportedDelayCode(input.code).code;
    const minutes = Math.round(Number(input.minutes));
    if (!Number.isFinite(minutes) || minutes < 1) return { ok: false, error: "Minutes are required." };
    const etd = (input.etd ?? "").replace(/\D/g, "").slice(0, 4);
    const rmk = asciiTelex(input.remarks ?? "");
    const packet = [`DLY ${code} ${minutes}`, etd.length === 4 ? `ETD ${etd}` : "", rmk ? `RMK ${rmk}` : ""].filter(Boolean).join(" ");
    return { ok: true, packet, parsed: parseDownlink(packet) };
  }
  if (input.kind === "mx") {
    const text = asciiTelex(input.text ?? "");
    if (!text) return { ok: false, error: "Write up the defect." };
    const ata = (input.ata ?? "").replace(/\D/g, "").slice(0, 2);
    const packet = ata ? `MX ATA ${ata} ${text}` : `MX ${text}`;
    return { ok: true, packet, parsed: parseDownlink(packet) };
  }
  if (input.kind === "fuel") {
    const amount = Number(input.amount);
    const unit = input.unit ?? "lb";
    if (!Number.isFinite(amount) || amount <= 0) return { ok: false, error: "Fuel quantity is required." };
    const airport = (input.airport ?? "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 4);
    const packet = `FUEL ${amount}${unit.toUpperCase()}${airport.length === 4 ? ` AT ${airport}` : ""}`;
    return { ok: true, packet, parsed: parseDownlink(packet) };
  }
  const text = asciiTelex(input.text ?? "");
  if (!text) return { ok: false, error: "Message is empty." };
  return { ok: true, packet: text, parsed: parseDownlink(text) };
}

export function asksForReply(packet: string): boolean {
  return /\bPLS\s+CFM\b/.test(packet.toUpperCase());
}

export function hoppieActiveTrip(mission: Pick<Mission, "status" | "departureIso">, now: Date): boolean {
  if (mission.status !== "pending" && mission.status !== "in-progress") return false;
  const std = Date.parse(mission.departureIso);
  if (!Number.isFinite(std)) return false;
  return Math.abs(std - now.getTime()) <= 3 * 60 * 60 * 1000;
}

function acarsId(now: Date, salt: string): string {
  return `acars-${now.getTime().toString(36)}-${salt.replace(/[^a-z0-9]/gi, "").slice(0, 12)}`;
}

const ACARS_KINDS = new Set(["delay", "etd_change", "slot", "pax_change", "wx_advisory", "fuel_advisory", "mx_call", "free_text", "downlink", "progress", "cpdlc"]);

export function hydrateAcarsLog(raw: unknown): AcarsMsg[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const out: AcarsMsg[] = [];
  const seen = new Set<string>();
  for (const row of raw) {
    if (!row || typeof row !== "object") continue;
    const src = row as Partial<AcarsMsg>;
    const id = String(src.id ?? "").trim().slice(0, 80);
    const missionId = String(src.missionId ?? "").trim().slice(0, 80);
    const packet = redactHoppieText(String(src.packet ?? "")).slice(0, 500);
    const atUtc = String(src.atUtc ?? "").trim().slice(0, 40);
    const kind = String(src.kind ?? "");
    if (!id || !missionId || !packet || !atUtc || !ACARS_KINDS.has(kind) || seen.has(id)) continue;
    const dir = src.dir === "down" ? "down" : "up";
    const status = src.status === "sent" || src.status === "failed" || src.status === "received" ? src.status : "queued";
    const channel = Array.isArray(src.channel) ? src.channel.filter((c): c is "hoppie" | "companion" => c === "hoppie" || c === "companion") : [];
    const parsed: Record<string, string> = {};
    if (src.parsed && typeof src.parsed === "object") {
      for (const [key, value] of Object.entries(src.parsed)) {
        if (typeof value !== "string") continue;
        if (!/^[a-z]{1,16}$/i.test(key)) continue;
        parsed[key] = redactHoppieText(value).slice(0, 180);
      }
    }
    seen.add(id);
    out.push({
      id,
      missionId,
      dir,
      channel: channel.length ? channel : [dir === "down" ? "hoppie" : "companion"],
      kind: kind as AcarsMsg["kind"],
      packet,
      atUtc,
      status,
      ...(src.error ? { error: redactHoppieText(String(src.error)).slice(0, 180) } : {}),
      ...(src.from ? { from: normCallsign(src.from).slice(0, 8) } : {}),
      ...(Object.keys(parsed).length ? { parsed } : {}),
    });
  }
  return out.length ? out : undefined;
}

export type AcarsUnmatched = { id: string; from: string; packet: string; type: string; atUtc: string };

export function hydrateAcarsUnmatched(raw: unknown): AcarsUnmatched[] {
  if (!Array.isArray(raw)) return [];
  const rows: AcarsUnmatched[] = [];
  for (const row of raw) {
    if (!row || typeof row !== "object") continue;
    const src = row as Partial<AcarsUnmatched>;
    const id = String(src.id ?? "").trim().slice(0, 80);
    const from = normCallsign(src.from).slice(0, 8);
    const packet = redactHoppieText(String(src.packet ?? "")).slice(0, 220);
    const atUtc = String(src.atUtc ?? "").trim().slice(0, 40);
    if (!id || !from || !packet || !atUtc) continue;
    rows.push({ id, from, packet, type: String(src.type ?? "telex").slice(0, 16).toLowerCase(), atUtc });
  }
  return dropStaleUnmatched(rows, new Date());
}

export function inboundAcarsMessage(
  input: { missionId: string; from: string; type: string; packet: string; channel?: AcarsChannel; id?: string; atUtc?: string },
  now = new Date(),
): AcarsMsg {
  const parsed = parseDownlink(input.packet, input.type);
  const kind: AcarsMsg["kind"] = parsed.kind === "progress" ? "progress" : parsed.kind === "cpdlc" ? "cpdlc" : parsed.kind === "text" ? "free_text" : "downlink";
  const id = String(input.id ?? "").replace(/[^a-zA-Z0-9:_-]/g, "").slice(0, 80);
  return {
    id: id || acarsId(now, input.from),
    missionId: input.missionId,
    dir: "down",
    channel: [input.channel ?? "hoppie"],
    kind,
    packet: redactHoppieText(input.packet).slice(0, 220),
    atUtc: input.atUtc && Date.parse(input.atUtc) ? new Date(input.atUtc).toISOString() : now.toISOString(),
    status: "received",
    from: normCallsign(input.from).slice(0, 8),
    parsed: downlinkToParsed(parsed),
  };
}

export function applyParsedDownlink(
  mission: Mission,
  parsed: Downlink,
  msgId: string,
  source: "acars" | "companion" = "acars",
  now = new Date(),
): { mission: Mission; squawk: { aircraftId: string; title: string; detail: string } | null; replan: boolean; blockHint?: string; error?: string } {
  if (parsed.kind === "delay") {
    if ((mission.delays ?? []).some((row) => row.acarsMsgId === msgId)) return { mission, squawk: null, replan: false };
    const filed = fileDelay(mission, { code: parsed.rawCode || parsed.code, minutes: Number(parsed.minutes), hhmm: parsed.etd, remarks: parsed.remarks, source, status: "pending", acarsMsgId: msgId }, now);
    if (!filed.ok) return { mission, squawk: null, replan: false, error: filed.error };
    return { mission: filed.mission, squawk: null, replan: true };
  }
  if (parsed.kind === "fuel") {
    if ((mission.fuelUplifts ?? []).some((row) => row.acarsMsgId === msgId)) return { mission, squawk: null, replan: false };
    const unit = parsed.unit === "KG" ? "kg" : parsed.unit === "L" ? "L" : parsed.unit === "USG" ? "USG" : "lb";
    const filed = fileFuelUplift(mission, { amount: Number(parsed.amount), unit, airportIcao: parsed.airport || mission.originIcao, reported: `${parsed.amount}${parsed.unit}`, source, status: "pending", acarsMsgId: msgId }, now);
    if (!filed.ok) return { mission, squawk: null, replan: false, error: filed.error };
    return { mission: filed.mission, squawk: null, replan: false };
  }
  if (parsed.kind === "mx") {
    const title = (parsed.ata ? `ATA ${parsed.ata} ${parsed.text}` : parsed.text).slice(0, 80);
    return { mission, squawk: mission.aircraftId ? { aircraftId: mission.aircraftId, title, detail: parsed.text.slice(0, 160) } : null, replan: true };
  }
  if (parsed.kind === "progress") {
    return { mission, squawk: null, replan: false, blockHint: parsed.summary };
  }
  return { mission, squawk: null, replan: false };
}

export function enqueueUplink(mission: Mission, draft: NotifyDraft, station: string, now = new Date()): Mission {
  if ((mission.acarsLog ?? []).some((row) => row.id === draft.id)) return mission;
  const rendered = renderUplink(draft.template, draft.params, station);
  const body = renderTelexBody(draft.template, draft.params);
  const packet = rendered.ok ? rendered.parts.join(" | ") : body.ok ? body.body : "";
  if (!packet) return mission;
  const msg: AcarsMsg = {
    id: draft.id,
    missionId: mission.id,
    dir: "up",
    channel: rendered.ok ? ["hoppie", "companion"] : ["companion"],
    kind: draft.template,
    packet: packet.slice(0, 500),
    atUtc: now.toISOString(),
    status: "queued",
    ...(!rendered.ok ? { error: rendered.error } : {}),
  };
  return { ...mission, acarsLog: [...(mission.acarsLog ?? []), msg] };
}

export type CompanionAcarsItem = { id: string; packet: string; atUtc: string };

export function hydrateCompanionAcars(raw: unknown): CompanionAcarsItem[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const out: CompanionAcarsItem[] = [];
  const seen = new Set<string>();
  for (const row of raw) {
    if (!row || typeof row !== "object") continue;
    const src = row as Partial<CompanionAcarsItem>;
    const id = String(src.id ?? "").replace(/[^a-zA-Z0-9:_-]/g, "").slice(0, 80);
    const packet = redactHoppieText(String(src.packet ?? "")).replace(/\s+/g, " ").trim().slice(0, 220);
    const atUtc = String(src.atUtc ?? "").trim().slice(0, 40);
    if (!id || !packet || !atUtc || seen.has(id)) continue;
    seen.add(id);
    out.push({ id, packet, atUtc });
  }
  return out.length ? out.slice(-24) : undefined;
}

function unionOps<T extends { id: string; status?: string }>(remote?: T[], local?: T[]): T[] | undefined {
  const map = new Map<string, T>();
  for (const row of remote ?? []) if (row.id) map.set(row.id, row);
  for (const row of local ?? []) {
    if (!row.id) continue;
    const prev = map.get(row.id);
    if (!prev || row.status === "confirmed") map.set(row.id, row);
  }
  const rows = [...map.values()];
  return rows.length ? rows : undefined;
}

/**
 * Pull keeps a local ACARS row the snapshot has not published yet, and files
 * each companion reply once. SimConnect times are left alone.
 */
export function foldPulledAcars(
  local: Mission | undefined,
  remote: Mission,
  now = new Date(),
): {
  mission: Mission;
  squawks: { id: string; aircraftId: string; title: string; detail: string }[];
  replan: boolean;
} {
  const logs = new Map<string, AcarsMsg>();
  for (const row of remote.acarsLog ?? []) logs.set(row.id, row);
  for (const row of local?.acarsLog ?? []) if (!logs.has(row.id)) logs.set(row.id, row);
  let mission: Mission = {
    ...remote,
    acarsLog: logs.size ? [...logs.values()] : undefined,
    delays: unionOps(remote.delays, local?.delays),
    fuelUplifts: unionOps(remote.fuelUplifts, local?.fuelUplifts),
    acarsBlockHint: remote.acarsBlockHint || local?.acarsBlockHint,
  };
  const pending = hydrateCompanionAcars([...(remote.companionAcars ?? []), ...(local?.companionAcars ?? [])]) ?? [];
  const squawks: { id: string; aircraftId: string; title: string; detail: string }[] = [];
  let replan = false;
  for (const item of pending) {
    if ((mission.acarsLog ?? []).some((row) => row.id === item.id)) continue;
    const message = inboundAcarsMessage(
      {
        missionId: mission.id,
        from: mission.flightNumber || mission.callsign,
        type: "telex",
        packet: item.packet,
        channel: "companion",
        id: item.id,
        atUtc: item.atUtc,
      },
      now,
    );
    const applied = applyParsedDownlink(mission, parseDownlink(item.packet, "telex"), message.id, "companion", now);
    mission = {
      ...applied.mission,
      acarsLog: [...(applied.mission.acarsLog ?? []), message],
      ...(applied.blockHint && !applied.mission.actualDepartureUtc ? { acarsBlockHint: applied.blockHint } : {}),
    };
    if (applied.squawk?.aircraftId) {
      squawks.push({
        id: `sqk-${item.id}`.slice(0, 80),
        aircraftId: applied.squawk.aircraftId,
        title: applied.squawk.title,
        detail: applied.squawk.detail,
      });
    }
    if (applied.replan) replan = true;
  }
  const { companionAcars: _pending, ...rest } = mission;
  return { mission: rest, squawks, replan };
}

/** Last Hoppie send on the desk, and the sends for one tail in the last hour. */
export function hoppieSendTimes(
  missions: readonly { aircraftId?: string; acarsLog?: AcarsMsg[] }[],
  aircraftId: string,
  nowMs: number,
): { lastSentAtMs: number | null; recentSentAtMs: number[] } {
  const recent: number[] = [];
  let last: number | null = null;
  for (const mission of missions) {
    for (const row of mission.acarsLog ?? []) {
      if (row.dir !== "up" || row.status !== "sent") continue;
      const at = Date.parse(row.atUtc);
      if (!Number.isFinite(at)) continue;
      if (last == null || at > last) last = at;
      if (mission.aircraftId === aircraftId && nowMs - at < 3_600_000) recent.push(at);
    }
  }
  return { lastSentAtMs: last, recentSentAtMs: recent };
}

export function markAcarsMessage(mission: Mission, id: string, status: "sent" | "failed" | "queued", error?: string): Mission {
  let changed = false;
  const acarsLog = (mission.acarsLog ?? []).map((row) => {
    if (row.id !== id) return row;
    changed = true;
    return { ...row, status, ...(error ? { error: redactHoppieText(error).slice(0, 180) } : { error: undefined }) };
  });
  return changed ? { ...mission, acarsLog } : mission;
}
